import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import * as D from "@radix-ui/react-dialog"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "@utils/tw"
import { useIsSSR, useRouterEvents } from "eddev/routing"
import { animate, AnimatePresence, AnimatePresenceProps, AnimationProps, motion, useMotionValue } from "motion/react"
import { PropsWithChildren, useEffect, useMemo, useState } from "react"
import { FLYING_CARPET_PANELS, FlyingCarpetTab, FlyingCarpetTabInfo } from "./carpet-panels"
import { Button } from "@components/ui/Button"

export function FlyingCarpet() {
  const carpet = useFlyingCarpet()
  const isDesktop = true
  const hydrating = useIsSSR()

  useRouterEvents((event) => {
    if (event.type === "clicked") carpet.close()
  })

  if (hydrating) return null

  if (isDesktop) {
    return <DesktopFrame />
  }
}

type AnchorProps = PropsWithChildren<{
  page: FlyingCarpetTab
}>

export function FlyingCarpetAnchor(props: AnchorProps) {
  return <Slot id={"flying-carpet-anchor-" + props.page}>{props.children}</Slot>
}

/**
 * On desktop, we're using a radix dialog box, which has better accessibility and focus management.
 */
function DesktopFrame() {
  const carpet = useFlyingCarpet()
  const [dialog, setDialog] = useState<HTMLDivElement | null>(null)

  const arrowLeft = useMotionValue(600)
  const arrowTop = useMotionValue(40)

  const header = useMemo(() => {
    return document.querySelector("#site-header")
  }, [])
  const anchor = useMemo(() => {
    if (!carpet.tab) return null
    return document.querySelector(`#flying-carpet-anchor-${carpet.tab}`)
  }, [carpet.tab])

  useEffect(() => {
    if (!header || !anchor || !dialog) {
      return
    }
    const update = () => {
      /**
       * Calculate the top position for the panel, as well as the arrow position
       */
      const headerBounds = header.getBoundingClientRect()
      const anchorBounds = anchor.getBoundingClientRect()
      const dialogBounds = dialog.getBoundingClientRect()
      const top = anchorBounds.bottom
      const arrow = anchorBounds.left + anchorBounds.width / 2 - (headerBounds.width - dialogBounds.width)

      dialog!.style.top = Math.max(top, 50) + "px"
      animate(arrowLeft, arrow, {})
    }

    // Observe the header and anchor bounds
    const ro = new ResizeObserver(update)
    ro.observe(header)
    ro.observe(anchor)

    return () => ro.disconnect()
  }, [carpet.isOpen, header, anchor, dialog])

  useEffect(() => {
    if (dialog) {
      animate(arrowTop, 0, {
        delay: 0.2,
      })
    } else {
      arrowTop.set(40)
    }
  }, [dialog])

  const panel = carpet.tab ? FLYING_CARPET_PANELS[carpet.tab] : null

  return (
    <D.Root
      open={carpet.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          carpet.close()
        }
      }}
    >
      <D.Portal>
        <D.Overlay className="radix-backdrop z-20 bg-black/30" />
        <D.Content
          ref={setDialog}
          className={cn(
            "fixed top-headerTopHeight left-auto right-0 bottom-0 z-20",
            "will-change-transform",
            "flex items-stretch justify-stretch",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full",
            "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-full",
            "!duration-500 transition-none",
            "w-full md:w-[25vw]",
            "!pointer-events-none",
          )}
        >
          {/* Arrow */}
          {/* <motion.div
            className="absolute top-[--arrow-top] left-[--arrow-left] -translate-x-1/2 z-10"
            style={{ "--arrow-left": arrowLeft, "--arrow-top": arrowTop } as any}
          >
            <svg
              className="fill-white stroke-primary/30 rotate-180"
              width="10"
              height="5"
              viewBox="0 0 30 10"
              preserveAspectRatio="none"
              aria-hidden
            >
              <polygon points="0,0 30,0 15,10"></polygon>
            </svg>
          </motion.div> */}

          {/* Modal content */}
          <div className="w-full bg-white pointer-events-auto z-20 overflow-hidden">
            {panel && (
              <div className="h-full w-full flex flex-col">
                {/* Top bar */}
                <div className="type-label-m p-2 relative border-b border-grey8 flex-none overflow-hidden flex items-center">
                  {/* Title */}
                  <Ticker tickerKey={panel.title}>
                    <D.Title>{panel.title}</D.Title>
                  </Ticker>

                  {/* Close Button */}
                  <D.Close asChild>
                    <Button icon="close" variant="ghost" size="icon" />
                  </D.Close>
                </div>

                {/* Main section */}
                <div className="flex-1 overflow-hidden flex items-center relative">
                  <Ticker tickerKey={carpet.tab!} animation="fade">
                    <FlyingCarpetContent tab={panel} />
                  </Ticker>
                </div>
              </div>
            )}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

function FlyingCarpetContent(props: { tab: FlyingCarpetTabInfo }) {
  const Component = props.tab.component

  if (Component) {
    return <Component />
  } else {
    return null
  }
}

const animationStyles = {
  ticker: {
    initial: { transform: "rotateX(90deg) translateZ(30px)" },
    animate: { transform: "rotateX(0deg) translateZ(30px)" },
    exit: {
      transform: "rotateX(-90deg) translateZ(30px)",
      transition: { duration: 0.2 },
    },
  },
  fade: {
    initial: { opacity: 0, y: "-1%", scale: 0.95 },
    animate: { opacity: 1, y: "0%", scale: 1, transition: { bounce: 0.2, delay: 0.1 } },
    exit: { opacity: 0, y: "1%", scale: 0.95, transition: { duration: 0.1 } },
  },
} satisfies Record<string, AnimationProps>

function Ticker(
  props: PropsWithChildren<{
    tickerKey: string | number
    mode?: AnimatePresenceProps["mode"]
    animation?: keyof typeof animationStyles
  }>,
) {
  const animProps = animationStyles[props.animation ?? "ticker"]
  return (
    <div style={{ perspective: "300px" }} className="absolute inset-0">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div key={props.tickerKey} {...animProps} className="absolute inset-0 flex items-center justify-center">
          {props.children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
